import { Button, Drawer, Dropdown, Form, Input, Select, Space } from "antd";
import { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { useAppSelector } from "../../../../app/store";
import ActionThreeDotIcon from "../../../../assets/icons/ActionThreeDotIcon";
import AddIcon from "../../../../assets/icons/AddIcon";
import EditIcon from "../../../../assets/icons/EditIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import SearchIcon from "../../../../assets/web/icons/SearchIcon";
import SuccessModal from "../../../../components/common/SuccessModal";
import CustomTable from "../../../../components/ui/CustomTable";
import Loader from "../../../../components/ui/Loader";
import {
  useCheckAccountNumberAvailabilityQuery,
  useCreateRouteMutation,
  useCreateVirtualAccountMutation,
  useGetAllAreaQuery,
  useGetAllRouteQuery,
  useGetAllTerritoryQuery,
  useGetAllZoneQuery,
  useGetSingleRouteQuery,
  useUpdateRouteMutation,
} from "../../../../features/cashCollection/geoSetup/geoSetupApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import useTable from "../../../../hooks/useTable";
import ConfirmModalLayout from "../../../../layouts/ConfirmModalLayout";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import {
  GetAllRouteDataParamsType,
  GetAllRouteDataType,
} from "../../../../types/cashCollection/geoSetup";
import { countParPageSerialNumber } from "../../../../utils/number";
import { capitalize } from "../../../../utils/string";
import { addKeyInArray } from "../../../../utils/utils";
import CashCollectionDetailsModal from "../../FieldOfficers/CashCollectionDetailsModal";
import AddEditManageModal from "../Modal/AddEditManageModal";

export default function RouteList() {
  const { user } = useAppSelector((state) => state.auth);

  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [accountNumber, setAccountNumber] = useState("");
  const [selectedTableId, setSelectedTableId] = useState<number | null>(null);
  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(null);
  const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null);
  const [bankOptions, setBankOptions] = useState<
    { label: string; value: string | number }[] | []
  >();
  const [form] = Form.useForm();

  const {
    open: openAddModal,
    handleOpen: handleAddOpen,
    handleClose: handleAddClose,
  } = useModal<undefined, "ADD">();

  const {
    open: openViewModal,
    handleOpen: handleViewOpen,
    handleClose: handleViewClose,
  } = useModal<any, "VIEW">();

  const {
    open: openConfirmModal,
    handleOpen: handleConfirmOpen,
    handleClose: handleConfirmClose,
  } = useModal<any, "CONFIRM">();

  const {
    open: openSuccessModal,
    handleOpen: handleSuccessOpen,
    handleClose: handleSuccessClose,
  } = useModal<undefined, "SUCCESS">();

  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    filter,
    handleFilter,
    handleSearch,
    keyword,
  } = useTable<GetAllRouteDataParamsType>();

  // Get all Routes
  const { isError, error, data, isLoading } = useGetAllRouteQuery({
    params: {
      routeName: keyword,
      skip: undefined,
      page,
      limit,
    },
  });

  // Get single Route
  const {
    data: getSingleRouteData,
    isError: getSingleRouteIsError,
    error: getSingleRouteError,
    isLoading: getSingleRouteIsLoading,
    isFetching: getSingleRouteIsFatching,
  } = useGetSingleRouteQuery(selectedId || "", {
    skip: !selectedId,
  });

  // Create Route
  const [
    createRoute,
    {
      isError: createRouteIsError,
      error: createRouteError,
      isLoading: createRouteIsLoading,
      isSuccess: createRouteIsSuccess,
    },
  ] = useCreateRouteMutation();

  // Update Route
  const [
    updateRoute,
    {
      isError: updateRouteIsError,
      error: updateRouteError,
      isLoading: updateRouteIsLoading,
      isSuccess: updateRouteIsSuccess,
    },
  ] = useUpdateRouteMutation();

  // Check Bank Account Availability
  // Run the query only when accountNumber is not empty
  const {
    data: availableData,
    isError: isAvailabilityError,
    error: availabilityError,
    isSuccess: isAvailabilitySuccess,
  } = useCheckAccountNumberAvailabilityQuery(
    { params: { msisdn: accountNumber } },
    { skip: !accountNumber }
  );

  // Get Zone
  const {
    isError: getAllZoneIsError,
    error: getAllZoneError,
    data: getAllZoneData,
    isLoading: getAllZoneIsLoading,
    isFetching: getAllZoneIsFetching,
  } = useGetAllZoneQuery({
    params: {
      page: 1,
      limit: 100000000,
    },
  });

  // Get all Areas
  const {
    isError: getAllAreaIsError,
    error: getAllAreaError,
    data: getAllAreaData,
    isLoading: getAllAreaIsLoading,
    isFetching: getAllAreaIsFetching,
  } = useGetAllAreaQuery(
    {
      params: {
        page: 1,
        limit: 100000000,
        zoneId: selectedZoneId ? selectedZoneId : undefined,
      },
    },
    {
      skip: !selectedZoneId,
    }
  );

  // Get all Territories
  const {
    isError: getAllTerritoryIsError,
    error: getAllTerritoryError,
    data: getAllTerritoryData,
    isLoading: getAllTerritoryIsLoading,
    isFetching: getAllTerritoryIsFetching,
  } = useGetAllTerritoryQuery(
    {
      params: {
        page: 1,
        limit: 100000000,
        areaId: selectedAreaId ? selectedAreaId : undefined,
      },
    },
    {
      skip: !selectedAreaId,
    }
  );

  const [
    createVirtualAccount,
    {
      isError: createVirtualAccountIsError,
      error: createVirtualAccountError,
      isLoading: createVirtualAccountIsLoading,
      isSuccess: createVirtualAccountIsSuccess,
    },
  ] = useCreateVirtualAccountMutation();

  // 🔹 Centralized error handling
  useError(isError, error, "Get all route fail");
  useError(getSingleRouteIsError, getSingleRouteError, "Get single route fail");
  useError(createRouteIsError, createRouteError, "Create route fail");
  useError(updateRouteIsError, updateRouteError, "Update route fail");
  useError(getAllAreaIsError, getAllAreaError, "Get all area fail");
  useError(getAllZoneIsError, getAllZoneError, "Get all zone fail");
  useError(
    isAvailabilityError,
    availabilityError,
    "Account number not eligible"
  );
  useError(
    getAllTerritoryIsError,
    getAllTerritoryError,
    "Get all territory fail"
  );
  useError(
    createVirtualAccountIsError,
    createVirtualAccountError,
    "Create virtual account fail"
  );

  const onFinish = (values: any) => {
    const selectedZone = getAllZoneData?.data?.rows?.find(
      (zone) => zone?.id === values?.zoneId
    );
    const selectedArea = getAllAreaData?.data?.rows?.find(
      (area) => area?.id === values?.areaId
    );
    const selectedTerritory = getAllTerritoryData?.data?.rows?.find(
      (territory) => territory?.id === values?.territoryId
    );

    const zoneName = selectedZone?.zoneName;
    const areaName = selectedArea?.areaName;
    const territoryName = selectedTerritory?.territoryName;

    const newData = {
      ...values,
      active: true,
    };

    const updateData = {
      ...values,
      zoneName: zoneName,
      areaName: areaName,
      territoryName: territoryName,
    };

    if (selectedId) {
      updateRoute({
        id: selectedId,
        data: updateData,
      });
    } else {
      createRoute(newData);
    }
  };

  const onFinishAccount = (values: any) => {
    const findBank = availableData?.payload?.availablePartners?.find(
      (b) => b.id === values?.bankName
    );

    const findRow = data?.data?.rows?.find((d) => d.id === selectedTableId);

    const newValues = {
      bankName: findBank?.name,
      bankId: Number(findBank?.id),
      bankCode: findBank?.keyword,
      accountNumber: values?.accountNumber,
      accountName: `${findRow?.territoryName}${findRow?.routeName}${user?.data?.company?.company_name}`,
      companyId: String(user?.data?.company?.id),
    };

    handleConfirmOpen({ type: "CONFIRM", data: newValues });
  };

  const showDrawer = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  const getModalTitle = () => {
    if (openAddModal.type === "ADD" && selectedId) {
      return "Rroute Successfully Updated!";
    }
    if (openAddModal.type === "ADD") {
      return "Route Successfully Added!";
    }
    if (openConfirmModal.type === "CONFIRM" && selectedId) {
      return "Virtual Account Successfully Updated!";
    }
    if (openConfirmModal.type === "CONFIRM") {
      return "Virtual Account Successfully Added!";
    }
    return "";
  };

  const handleOkay = () => {
    handleCloseModal();
    handleSuccessClose();
  };

  const handleCloseModal = () => {
    setSelectedId(null);
    setSelectedZoneId(null);
    setSelectedAreaId(null);
    setSelectedTableId(null);
    form.setFieldsValue({});
    form.resetFields();
    handleAddClose();
    onClose();
    handleConfirmClose();
  };

  const handleSendToApprove = () => {
    createVirtualAccount({ data: openConfirmModal?.data, id: selectedTableId });
  };

  useEffect(() => {
    if (getSingleRouteData?.data && selectedId) {
      form.setFieldsValue({
        zoneId: getSingleRouteData?.data?.zoneId,
        areaId: getSingleRouteData?.data?.areaId,
        territoryId: getSingleRouteData?.data?.territoryId,
        routeName: capitalize(getSingleRouteData?.data?.routeName),
        accountName: getSingleRouteData?.data?.bank?.accountName,
        accountNumber: getSingleRouteData?.data?.bank?.accountNumber,
        routingNumber: getSingleRouteData?.data?.bank?.routingNumber,
        bankName: getSingleRouteData?.data?.bank?.bankName,
        branchName: getSingleRouteData?.data?.bank?.branchName,
      });
      setSelectedZoneId(Number(getSingleRouteData?.data?.zoneId));
      setSelectedAreaId(Number(getSingleRouteData?.data?.areaId));
    }
  }, [
    getSingleRouteData?.data,
    selectedId,
    setSelectedZoneId,
    setSelectedAreaId,
    form,
  ]);

  useEffect(() => {
    if (isAvailabilityError) {
      // Clear the bankName field
      form.setFieldsValue({ bankName: undefined });
      setBankOptions([]);

      // Trigger validation for accountNumber with a custom error
      form.validateFields(["accountNumber"]).catch(() => {
        form.setFields([
          {
            name: "accountNumber",
            errors: ["Account number not eligible or invalid"],
          },
        ]);
      });
    }
  }, [isAvailabilityError, form]);

  useEffect(() => {
    if (isAvailabilitySuccess) {
      setBankOptions(
        availableData?.payload?.availablePartners?.map((p) => ({
          label: p.name,
          value: p.id,
        }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAvailabilitySuccess, form]);
  useEffect(() => {
    if (createRouteIsSuccess) {
      handleSuccessOpen({ type: "SUCCESS" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createRouteIsSuccess]);

  useEffect(() => {
    if (updateRouteIsSuccess) {
      handleSuccessOpen({ type: "SUCCESS" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateRouteIsSuccess]);

  useEffect(() => {
    if (createVirtualAccountIsSuccess) {
      handleSuccessOpen({ type: "SUCCESS" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createVirtualAccountIsSuccess]);

  const detailsViewData = openViewModal?.data;

  const detailsData = [
    { label: "Bank Name", value: detailsViewData?.bankName ?? "N/A" },
    { label: "Branch", value: detailsViewData?.branchName ?? "N/A" },
    {
      label: "Virtual Account",
      value: detailsViewData?.virtualAccount ?? "N/A",
    },
    { label: "Account Number", value: detailsViewData?.accountNumber ?? "N/A" },
    { label: "Account Name", value: detailsViewData?.accountName ?? "N/A" },
    { label: "Routing Number", value: detailsViewData?.routingNumber ?? "N/A" },
    { label: "Status", value: "Active" },
  ];

  const columns: ColumnsType<GetAllRouteDataType> = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },

    {
      title: <div className="text-nowrap">Field Officer</div>,
      dataIndex: "assignedUserData",
      align: "center",
      render: (val) => {
        return val?.username ? capitalize(val?.username) : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Email</div>,
      dataIndex: "assignedUserData",
      align: "center",
      render: (val) => {
        return val?.email ? val?.email : "N/A";
      },
    },

    {
      title: <div className="text-nowrap">Zone</div>,
      dataIndex: "zoneName",
      align: "center",
      render: (val) => {
        return val ? capitalize(val) : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Area</div>,
      dataIndex: "areaName",
      align: "center",
      render: (val) => {
        return val ? capitalize(val) : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Territory</div>,
      dataIndex: "territoryName",
      align: "center",
      render: (val) => {
        return val ? capitalize(val) : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Route</div>,
      dataIndex: "routeName",
      align: "center",
      render: (val) => {
        return val ? capitalize(val) : "N/A";
      },
    },

    {
      title: <div className="text-nowrap">Virtual Account Number</div>,
      dataIndex: "virtualAccountNumber",
      align: "center",
      render: (val) => {
        return val ? val : "N/A";
      },
    },

    {
      title: <div className="text-nowrap">Virtual Account</div>,
      dataIndex: "bank",
      align: "center",
      render: (val, record: any) => {
        return val ? (
          <Button
            type="default"
            size="small"
            style={{
              color: "#1B4DFF",
              background: "transparent",
              border: "none",
              boxShadow: "none",
            }}
            onClick={() => handleViewOpen({ type: "VIEW", data: val })}
          >
            View
          </Button>
        ) : (
          <Button
            className="!border-none !bg-transparent !text-[#FE7235] hover:!bg-transparent"
            type="default"
            size="small"
            onClick={() => {
              setSelectedTableId(record?.id);
              showDrawer();
            }}
          >
            Add
          </Button>
        );
      },
    },
    {
      title: <div className="whitespace-nowrap !w-[110px]">Status</div>,
      dataIndex: "active",
      align: "center",
      render: (val: boolean) => {
        const status = val ? "Active" : "Inactive";

        const statusClasses: Record<string, string> = {
          Active: "bg-green-100 text-green-700",
          Inactive: "bg-red-100 text-red-700",
        };

        return (
          <span
            className={`px-2 py-1 rounded text-xs font-medium ${statusClasses[status]}`}
          >
            {status}
          </span>
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "id",
      align: "center",
      render: (val: number, record: any) => {
        const items = [
          {
            key: "edit",
            label: (
              <div
                className="flex items-center gap-2 justify-start text-slate-700 text-sm font-medium"
                onClick={() => {
                  setSelectedId(val);
                  handleAddOpen({ type: "ADD" });
                }}
              >
                <EditIcon />
                <span>Edit</span>
              </div>
            ),
          },
          ...(record?.bank
            ? [
                {
                  key: "editAccount",
                  label: (
                    <div
                      className="flex items-center gap-2 justify-start text-slate-700 text-sm font-medium"
                      onClick={() => {
                        setSelectedId(val);
                        setSelectedTableId(val);
                        showDrawer();
                      }}
                    >
                      <EditIcon />
                      <span>Update Virtual Account</span>
                    </div>
                  ),
                },
              ]
            : []),
        ];

        return (
          <div className="flex justify-center items-center gap-2">
            <Dropdown
              menu={{
                items,
                className: "min-w-[170px]",
              }}
              trigger={["click"]}
              placement="bottomRight"
              arrow={{ pointAtCenter: true }}
            >
              <Button
                className="action-button disabled:!bg-[#ddd] !border-none"
                size="small"
                icon={<ActionThreeDotIcon />}
              />
            </Dropdown>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <SidebarLayout title="GEO Set-up">
        <CustomTable
          loading={isLoading}
          limit={limit}
          page={page}
          columns={columns}
          total={data?.data?.count}
          dataSource={addKeyInArray(data?.data?.rows)}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          filterSection={
            <>
              <Input
                suffix={<SearchIcon />}
                placeholder="Search Route"
                style={{ width: 215 }}
                allowClear
                onChange={handleSearch}
              />

              <Button
                type="primary"
                className="middle-button"
                icon={<AddIcon />}
                onClick={() => handleAddOpen({ type: "ADD" })}
              >
                New Route
              </Button>
            </>
          }
        />
      </SidebarLayout>

      {openAddModal?.type === "ADD" && (
        <AddEditManageModal
          open={openAddModal?.open}
          handleClose={handleCloseModal}
        >
          <div>
            <div className="justify-start text-zinc-700 text-xl font-medium">
              {selectedId ? "Update Route" : "Add New Route"}
            </div>

            {getSingleRouteIsLoading ||
            getSingleRouteIsFatching ||
            getAllZoneIsLoading ||
            getAllZoneIsFetching ||
            getAllAreaIsLoading ||
            getAllAreaIsFetching ||
            getAllTerritoryIsLoading ||
            getAllTerritoryIsFetching ? (
              <Loader />
            ) : (
              <div className="mt-5">
                <Form
                  className="custom-form"
                  layout="vertical"
                  requiredMark="optional"
                  size="large"
                  onFinish={onFinish}
                  form={form}
                >
                  <Form.Item
                    label="Zone"
                    name="zoneId"
                    rules={[{ required: true, message: "Please enter zone" }]}
                  >
                    <Select
                      placeholder="Select Zone"
                      suffixIcon={<SelectDownIcon />}
                      showSearch
                      allowClear
                      className="w-full"
                      disabled={!!selectedId}
                      options={
                        getAllZoneData?.data?.rows?.map((val: any) => ({
                          label: capitalize(val?.zoneName),
                          value: val?.id,
                        })) || []
                      }
                      onChange={(value) => setSelectedZoneId(value)}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Area"
                    name="areaId"
                    rules={[{ required: true, message: "Please enter area" }]}
                  >
                    <Select
                      placeholder="Select Area"
                      suffixIcon={<SelectDownIcon />}
                      showSearch
                      allowClear
                      className="w-full"
                      disabled={!selectedZoneId || !!selectedId}
                      options={
                        getAllAreaData?.data?.rows?.map((val: any) => ({
                          label: capitalize(val?.areaName),
                          value: val?.id,
                        })) || []
                      }
                      onChange={(value) => setSelectedAreaId(value)}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Territory"
                    name="territoryId"
                    rules={[
                      {
                        required: true,
                        message: "Please enter territory",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select Territory"
                      suffixIcon={<SelectDownIcon />}
                      showSearch
                      allowClear
                      className="w-full"
                      disabled={!selectedAreaId || !!selectedId}
                      options={
                        getAllTerritoryData?.data?.rows?.map((val: any) => ({
                          label: capitalize(val?.territoryName),
                          value: val?.id,
                        })) || []
                      }
                    />
                  </Form.Item>

                  <Form.Item
                    label="Route Name"
                    name="routeName"
                    rules={[
                      { required: true, message: "Please enter route name" },
                    ]}
                  >
                    <Input placeholder="Enter Route Name" />
                  </Form.Item>

                  <Space className="w-full mt-5" size={12}>
                    <Button
                      className="normal-dark-button modal-button !h-[40px]"
                      onClick={handleCloseModal}
                      size="middle"
                    >
                      Cancel
                    </Button>

                    <Button
                      className="modal-button !h-[40px]"
                      type="primary"
                      size="middle"
                      htmlType="submit"
                      loading={createRouteIsLoading || updateRouteIsLoading}
                    >
                      {selectedId ? "Update Route" : "Add Route"}
                    </Button>
                  </Space>
                </Form>
              </div>
            )}
          </div>
        </AddEditManageModal>
      )}

      {openViewModal?.type === "VIEW" && (
        <CashCollectionDetailsModal
          open={openViewModal?.open}
          handleClose={handleViewClose}
        >
          <div>
            <div className="justify-start text-zinc-700 text-xl font-medium">
              Bank Details
            </div>

            <div className="pr-4 mt-5">
              {detailsData.map((item, index) => (
                <div
                  key={index}
                  className={`flex justify-between py-2 ${
                    index !== detailsData.length - 1
                      ? "border-b border-[#D7DFE9]"
                      : ""
                  }`}
                >
                  <span className="text-slate-500 text-base font-normal">
                    {item.label}
                  </span>
                  <span
                    className={`text-base font-normal px-2 py-1 rounded ${
                      item.value === "Active"
                        ? "bg-green-100 text-green-700"
                        : item.value === "Inactive"
                        ? "bg-red-100 text-red-700"
                        : "text-zinc-700"
                    }`}
                  >
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </CashCollectionDetailsModal>
      )}

      {openConfirmModal?.type === "CONFIRM" && (
        <ConfirmModalLayout
          title={`${
            selectedId ? "Update Virtual Account?" : "Add New Virtual Account?"
          }`}
          subTitle={`${
            selectedId
              ? "Are you sure you want to Update Virtual Account?"
              : "Are you sure you want to add new Virtual Account?"
          }`}
          buttonName={`${selectedId ? "Update Account" : "Add Account"}`}
          handleButton={handleSendToApprove}
          open={openConfirmModal?.open}
          handleClose={handleConfirmClose}
          action
          buttonBgDeep
          loading={createVirtualAccountIsLoading}
        ></ConfirmModalLayout>
      )}

      {openSuccessModal.type === "SUCCESS" && (
        <SuccessModal
          open={openSuccessModal?.open}
          handleClose={handleSuccessClose}
          handleOkay={handleOkay}
          data={openSuccessModal?.data}
          title={getModalTitle()}
        />
      )}

      <Drawer open={open} width={400} onClose={onClose}>
        <div className="justify-start text-zinc-700 text-xl font-semibold">
          {selectedId ? "Update Virtual Account" : " Add Virtual Account"}
        </div>

        {(selectedId && getSingleRouteIsLoading) ||
        (selectedId && getSingleRouteIsFatching) ? (
          <Loader />
        ) : (
          <div className="mt-5">
            <Form
              className="custom-form"
              layout="vertical"
              requiredMark="optional"
              size="large"
              onFinish={onFinishAccount}
              form={form}
            >
              <Form.Item
                label="Account Number"
                name="accountNumber"
                rules={[
                  { required: true, message: "Please enter account number" },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();

                      if (Number(value) < 0)
                        return Promise.reject(
                          "Account number cannot be negative"
                        );
                      if (value.includes("."))
                        return Promise.reject(
                          "Account number cannot be decimal"
                        );
                      if (!/^\d+$/.test(value))
                        return Promise.reject(
                          "Account number must contain only numbers"
                        );
                      if (value.length < 13)
                        return Promise.reject(
                          "Account number must be at least 13 digits"
                        );

                      // Availability check
                      if (isAvailabilityError) {
                        return Promise.reject(
                          "Account number not eligible or invalid"
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  onBlur={(e) => setAccountNumber(e.target.value.trim())}
                  type="number"
                  onChange={(e) => {
                    form.resetFields(["bankName"]);
                    if (!e.target.value) setBankOptions([]);
                    if (e?.target?.value?.length === 13)
                      setAccountNumber(e.target.value.trim());
                  }}
                  placeholder="Enter Account Number"
                  className="w-full"
                />
              </Form.Item>

              <Form.Item
                label="Bank Name"
                name="bankName"
                rules={[
                  {
                    required: (bankOptions?.length ?? 0) > 0,
                    message: "Please enter bank name",
                  },
                ]}
              >
                <Select
                  placeholder={
                    bankOptions?.length
                      ? "Select Bank Name"
                      : "No banks available"
                  }
                  suffixIcon={<SelectDownIcon />}
                  showSearch
                  allowClear
                  className="w-full"
                  optionFilterProp="label"
                  disabled={false} // keep it enabled for validation
                  options={bankOptions}
                  notFoundContent={
                    bankOptions?.length ? undefined : "No banks available"
                  }
                />
              </Form.Item>

              <Space className="w-full mt-5" size={12}>
                <Button
                  className="normal-dark-button modal-button !h-[40px]"
                  size="middle"
                  onClick={() => {
                    form.resetFields();
                    onClose();
                    setBankOptions([]);
                  }}
                >
                  Cancel
                </Button>

                <Button
                  className="modal-button !h-[40px]"
                  type="primary"
                  size="middle"
                  htmlType="submit"
                >
                  {selectedId ? "Update Account" : "Add Account"}
                </Button>
              </Space>
            </Form>
          </div>
        )}
      </Drawer>
    </>
  );
}
